import { Anchor, Group, Skeleton, Text } from '@mantine/core';
import { AppWindow, ArrowRight, MonitorPlay, User } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { USER_ROUTES } from '../../../../routes/userRoutes';
import { SessionCategoryCell } from '../../../sessions/components/SessionCategoryCell';
import { getScenarioDisplayLabel } from '../../../../data/maps';
import { SessionCard } from '../SessionCard';
import classes from './SessionInfoCard.module.css';

export type SessionInfoCardProps = {
  patientCode?: string;
  patientId?: number;
  appType?: string;
  scenario?: string;
  videoName?: string;
  source?: string;
  isLoading?: boolean;
};

const isValidValue = (value: string | undefined | null): boolean => {
  if (!value || value.trim() === '') {
    return false;
  }
  if (value === '-') {
    return false;
  }
  const lowerValue = value.toLowerCase().trim();
  if (lowerValue === 'video non trovato') {
    return false;
  }
  return true;
};

export const SessionInfoCard: React.FC<SessionInfoCardProps> = ({
  patientCode,
  patientId,
  appType,
  scenario,
  videoName,
  source,
  isLoading = false,
}: SessionInfoCardProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <SessionCard>
        {/* Utente */}
        <Group gap='sm'>
          <User className='mr-2' />
          <Text size='sm' c='dimmed'>
            Utente:
          </Text>
          <Skeleton height={20} width={100} />
        </Group>

        {/* App/Tipologia */}
        <Group gap='sm'>
          <AppWindow className='mr-2' />
          <Text size='sm' c='dimmed'>
            Categoria:
          </Text>
          <Skeleton height={20} width={80} />
        </Group>

        {/* Esperienza/Scenario */}
        <Group gap='sm'>
          <MonitorPlay className='mr-2' strokeWidth={1.5} />
          <Text size='sm' c='dimmed'>
            Esperienza:
          </Text>
          <Skeleton height={20} width={120} />
        </Group>
      </SessionCard>
    );
  }

  const experienceValue = scenario !== 'esperienza-vr' ? scenario : videoName;
  const hasValidExperience =
    appType !== 'body_image' && isValidValue(experienceValue);
  const canLinkToUser =
    isValidValue(patientCode) && typeof patientId === 'number' && patientId > 0;
  const experienceLabel =
    scenario !== 'esperienza-vr'
      ? getScenarioDisplayLabel(appType, scenario, videoName)
      : t(videoName || '');

  return (
    <SessionCard>
      {/* Utente */}
      {isValidValue(patientCode) && (
        <Group gap='sm' wrap='nowrap' align='center'>
          <User className='mr-2' />
          <Text size='sm' c='dimmed'>
            Utente:
          </Text>
          {canLinkToUser ? (
            <Anchor
              component={Link}
              to={USER_ROUTES.detail(patientId)}
              className={classes.userLink}
              underline='never'
              aria-label={t(
                'session_user_go_to_detail_aria',
                'Vai al dettaglio utente {{name}}',
                { name: patientCode }
              )}
            >
              <Text fw={600} component='span' className={classes.userLinkName}>
                {patientCode}
              </Text>
              <span className={classes.userLinkHint}>
                <ArrowRight size={14} aria-hidden />
                <Text component='span' size='xs' fw={500}>
                  {t('session_user_go_to_detail', 'Vai al dettaglio')}
                </Text>
              </span>
            </Anchor>
          ) : (
            <Text fw={600}>{patientCode}</Text>
          )}
        </Group>
      )}

      {/* App/Tipologia */}
      {isValidValue(appType) && (
        <Group gap='sm' wrap='nowrap' align='center'>
          <AppWindow className='mr-2' />
          <Text size='sm' c='dimmed'>
            Categoria:
          </Text>
          <SessionCategoryCell
            type={appType}
            scenario={scenario}
            source={source}
          />
        </Group>
      )}

      {/* Esperienza/Scenario */}
      {hasValidExperience && (
        <Group gap='sm'>
          <MonitorPlay className='mr-2' strokeWidth={1.5} />
          <Text size='sm' c='dimmed'>
            {appType === 'performance' ? 'Scenario:' : 'Esperienza:'}
          </Text>
          <Text fw={600}>{experienceLabel}</Text>
        </Group>
      )}
    </SessionCard>
  );
};
